import React, { useState } from 'react';
import { usePrice }         from '../../hooks/usePrice';
import { useJarvisContext, A } from '../../context/JarvisContext';
import {
  getTodayLesson,
  getStats,
  getRules,
  getBehaviors,
  getPatterns,
  getTrades,
  getJarvisMemoryData,
} from '../../services/storage';
import { getBrainReport }      from '../../engines/jarvisBrain';
import { calcEvolutionStage, EVOLUTION_STAGES } from '../../engines/predictionTracker';
import { getTodayDate, getDayOfWeek, timeUntilLondon } from '../../utils/dateUtils';
import { analyzeAllTimeframes } from '../../services/scriptAnalysis';
import { THEME } from '../../styles/theme';

// ── Tiny reusable card ────────────────────────────────────────────────
function Card({ children, style, glow }) {
  return (
    <div style={{
      background:   THEME.colors.card,
      border:       `1px solid ${glow ? `${glow}33` : THEME.colors.border}`,
      borderRadius: '12px',
      padding:      '16px',
      boxShadow:    glow ? `0 0 20px ${glow}11` : 'none',
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardTitle({ children, color }) {
  return (
    <div style={{
      fontSize:     '10px',
      fontWeight:   '700',
      letterSpacing: '2px',
      color:        color || THEME.colors.textMuted,
      fontFamily:   THEME.fonts.hud,
      marginBottom: '10px',
      textTransform: 'uppercase',
    }}>
      {children}
    </div>
  );
}

function BigNumber({ value, label, color, suffix }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize:     '28px',
        fontWeight:   '800',
        color:        color || THEME.colors.blue,
        fontFamily:   THEME.fonts.hud,
        letterSpacing: '1px',
        lineHeight:   1,
      }}>
        {value}{suffix}
      </div>
      <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginTop: '4px', letterSpacing: '1px' }}>
        {label}
      </div>
    </div>
  );
}

function ProgressBar({ value, max, color }) {
  const pct = Math.min(100, Math.round((value / (max || 1)) * 100));
  return (
    <div style={{ height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden', marginTop: '6px' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color || THEME.colors.blue, borderRadius: '2px', transition: 'width 0.6s ease' }} />
    </div>
  );
}

// ── Price Hero ─────────────────────────────────────────────────────────
function PriceHero({ onNavigate }) {
  const { priceDisplay, changeDisplay, isUp, isDown, session, priceLoading, priceError, refetch } = usePrice();
  const color = isUp ? THEME.colors.green : isDown ? THEME.colors.red : THEME.colors.text;
  const minutesUntilLondon = timeUntilLondon();

  return (
    <Card glow={color} style={{ gridColumn: 'span 2' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted, letterSpacing: '3px', fontFamily: THEME.fonts.hud, marginBottom: '6px' }}>
            XAU / USD  ·  SPOT GOLD
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <div style={{ fontSize: '42px', fontWeight: '900', color, fontFamily: THEME.fonts.hud, letterSpacing: '1px', lineHeight: 1 }}>
              {priceLoading ? '...' : priceDisplay}
            </div>
            {changeDisplay && (
              <div style={{ fontSize: '18px', fontWeight: '700', color }}>
                {isUp ? '▲' : '▼'} {changeDisplay}
              </div>
            )}
          </div>
          {priceError && (
            <div style={{ fontSize: '11px', color: THEME.colors.red, marginTop: '4px' }}>
              ⚠️ {priceError} — <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={refetch}>retry</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {session && (
            <div style={{
              padding:      '6px 14px',
              borderRadius: '20px',
              border:       `1px solid ${session.color || THEME.colors.blue}44`,
              background:   `${session.color || THEME.colors.blue}11`,
              color:        session.color || THEME.colors.blue,
              fontSize:     '13px',
              fontWeight:   '700',
              fontFamily:   THEME.fonts.hud,
            }}>
              {session.emoji} {session.name}
            </div>
          )}
          {minutesUntilLondon > 0 && (
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted, letterSpacing: '1px' }}>
              London in {minutesUntilLondon}m
            </div>
          )}
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>
            {getTodayDate()} · {getDayOfWeek()}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── JARVIS Evolution Card ──────────────────────────────────────────────
function EvolutionCard() {
  const memory    = getJarvisMemoryData();
  const stats     = getStats();
  const stage     = calcEvolutionStage(memory, stats.winRate || 0);
  const stageInfo = EVOLUTION_STAGES[stage];
  const nextStage = EVOLUTION_STAGES[stage + 1];
  const discussions = memory.total_discussions || 0;
  const nextTarget  = stage === 1 ? 50 : stage === 2 ? 150 : stage === 3 ? 300 : 500;

  return (
    <Card glow={THEME.colors.gold}>
      <CardTitle color={THEME.colors.gold}>JARVIS EVOLUTION</CardTitle>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{
          width:        '48px', height: '48px', borderRadius: '50%',
          background:   `${stageInfo?.color || THEME.colors.gold}18`,
          border:       `2px solid ${stageInfo?.color || THEME.colors.gold}44`,
          display:      'flex', alignItems: 'center', justifyContent: 'center',
          fontSize:     '22px', flexShrink: 0,
        }}>
          {stage === 5 ? '🌟' : stage === 4 ? '💎' : stage === 3 ? '🔮' : stage === 2 ? '📈' : '📚'}
        </div>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '800', color: stageInfo?.color || THEME.colors.gold, fontFamily: THEME.fonts.hud }}>
            STAGE {stage} — {stageInfo?.name?.toUpperCase()}
          </div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginTop: '2px' }}>{stageInfo?.desc}</div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '4px' }}>
        <span>{discussions} sessions</span>
        {nextStage && <span>Next: {nextTarget} sessions</span>}
      </div>
      <ProgressBar value={discussions} max={nextTarget} color={stageInfo?.color || THEME.colors.gold} />
    </Card>
  );
}

// ── Trading Stats Card ─────────────────────────────────────────────────
function StatsCard({ onNavigate }) {
  const stats = getStats();
  return (
    <Card>
      <CardTitle>TRADING PERFORMANCE</CardTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <BigNumber value={stats.winRate || 0}  label="WIN RATE"    color={stats.winRate >= 60 ? THEME.colors.green : THEME.colors.red} suffix="%" />
        <BigNumber value={stats.totalTrades || 0} label="TRADES"  color={THEME.colors.blue} />
        <BigNumber value={stats.avgRR || '0.00'} label="AVG R:R"  color={THEME.colors.gold} />
        <BigNumber value={stats.wins || 0}       label="WINS"     color={THEME.colors.green} />
      </div>
      {stats.totalTrades === 0 && (
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, textAlign: 'center', marginTop: '12px' }}>
          Log trades in the Journal to track performance
        </div>
      )}
    </Card>
  );
}

// ── Knowledge Stats Card ───────────────────────────────────────────────
function KnowledgeCard() {
  const rules       = getRules();
  const behaviors   = getBehaviors();
  const patterns    = getPatterns();
  const activeRules = rules.filter(r => r.status === 'active').length;
  const highBeh     = behaviors.filter(b => ['HIGH', 'CONFIRMED'].includes(b.confidence)).length;
  const strongPat   = patterns.filter(p => ['STRONG', 'CONFIRMED'].includes(p.confidence)).length;

  const items = [
    { label: 'Active Rules',         val: activeRules, total: rules.length,     color: THEME.colors.blue },
    { label: 'Confirmed Behaviors',  val: highBeh,     total: behaviors.length, color: THEME.colors.gold },
    { label: 'Strong Patterns',      val: strongPat,   total: patterns.length,  color: THEME.colors.green },
  ];

  return (
    <Card>
      <CardTitle>JARVIS KNOWLEDGE BASE</CardTitle>
      {items.map(item => (
        <div key={item.label} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '3px' }}>
            <span>{item.label}</span>
            <span style={{ color: item.color, fontWeight: '700', fontFamily: THEME.fonts.hud }}>
              {item.val} / {item.total}
            </span>
          </div>
          <ProgressBar value={item.val} max={Math.max(item.total, 1)} color={item.color} />
        </div>
      ))}
      {rules.length === 0 && behaviors.length === 0 && (
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, textAlign: 'center', padding: '8px 0' }}>
          Chat with JARVIS to start building your knowledge base
        </div>
      )}
    </Card>
  );
}

// ── Brain Report Card ──────────────────────────────────────────────────
function BrainCard() {
  const brain = getBrainReport();
  const { strategies, hypotheses } = brain;

  return (
    <Card glow={THEME.colors.blue}>
      <CardTitle color={THEME.colors.blue}>JARVIS SELF-BRAIN</CardTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        <BigNumber value={strategies.proven}  label="PROVEN STRATS"  color={THEME.colors.green} />
        <BigNumber value={strategies.active}  label="TOTAL STRATS"   color={THEME.colors.blue} />
        <BigNumber value={hypotheses.confirmed} label="CONFIRMED HYP" color={THEME.colors.gold} />
      </div>
      {strategies.list.slice(0, 3).map(s => (
        <div key={s.id} style={{
          display:      'flex', alignItems: 'center', justifyContent: 'space-between',
          padding:      '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
          fontSize:     '12px',
        }}>
          <span style={{ color: THEME.colors.text, flex: 1 }}>{s.title}</span>
          <span style={{
            padding:      '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700',
            background:   s.status === 'STRONG' ? 'rgba(0,255,136,0.1)' : s.status === 'PROVEN' ? 'rgba(0,212,255,0.1)' : 'rgba(255,255,255,0.05)',
            color:        s.status === 'STRONG' ? THEME.colors.green : s.status === 'PROVEN' ? THEME.colors.blue : THEME.colors.textMuted,
          }}>
            {s.status}
          </span>
        </div>
      ))}
      {strategies.total === 0 && (
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
          Need 5+ closed trades for JARVIS to auto-generate strategies
        </div>
      )}
    </Card>
  );
}

// ── Today's Lesson Card ────────────────────────────────────────────────
function TodayCard({ onNavigate }) {
  const lesson = getTodayLesson();
  if (!lesson) {
    return (
      <Card style={{ gridColumn: 'span 2' }}>
        <CardTitle>TODAY'S TEACHING</CardTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '14px', color: THEME.colors.text, marginBottom: '4px' }}>
              No recap submitted yet today.
            </div>
            <div style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
              Teach JARVIS what happened in the market today — it learns from every session.
            </div>
          </div>
          <button
            onClick={() => onNavigate('daily')}
            style={{
              padding:      '10px 20px', borderRadius: '8px', border: 'none',
              background:   THEME.colors.gold, color: '#000',
              fontSize:     '13px', fontWeight: '800', cursor: 'pointer',
              fontFamily:   THEME.fonts.hud, letterSpacing: '1px', whiteSpace: 'nowrap',
            }}
          >
            📚 TEACH JARVIS
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ gridColumn: 'span 2' }} glow={THEME.colors.green}>
      <CardTitle color={THEME.colors.green}>TODAY'S TEACHING — {lesson.date}</CardTitle>
      <div style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.7', marginBottom: '10px' }}>
        {lesson.jarvis_summary || lesson.market_behavior}
      </div>
      {lesson.jarvis_takeaway && (
        <div style={{
          padding: '10px 12px', background: 'rgba(0,255,136,0.05)',
          borderRadius: '8px', borderLeft: `3px solid ${THEME.colors.green}`,
          fontSize: '13px', color: THEME.colors.text, lineHeight: '1.6',
        }}>
          💡 {lesson.jarvis_takeaway}
        </div>
      )}
      {lesson.tomorrow_watch && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: THEME.colors.gold }}>
          👀 Tomorrow: {lesson.tomorrow_watch}
        </div>
      )}
    </Card>
  );
}

// ── Recent Trades Card ─────────────────────────────────────────────────
function RecentTradesCard({ onNavigate }) {
  const trades = getTrades().slice(-5).reverse();

  const resColor = r => r === 'WIN' ? THEME.colors.green : r === 'LOSS' ? THEME.colors.red : r === 'BE' ? '#ffd700' : THEME.colors.textMuted;
  const dirColor = d => d === 'BUY' ? THEME.colors.green : THEME.colors.red;
  const gradeColor = g => g === 'A+' || g === 'A' ? THEME.colors.green : g === 'B' ? '#ffd700' : THEME.colors.red;

  if (!trades.length) {
    return (
      <Card style={{ gridColumn: 'span 2' }}>
        <CardTitle>RECENT TRADES (Last 5)</CardTitle>
        <div style={{ fontSize: '13px', color: THEME.colors.textMuted, textAlign: 'center', padding: '20px 0' }}>
          No trades logged yet. Log your first trade in Journal.
        </div>
        <button onClick={() => onNavigate('journal')} style={{
          width: '100%', padding: '10px', background: 'rgba(0,212,255,0.06)',
          border: '1px solid rgba(0,212,255,0.15)', borderRadius: '8px',
          color: THEME.colors.blue, fontSize: '13px', cursor: 'pointer', fontWeight: '700',
        }}>
          + Log Trade in Journal →
        </button>
      </Card>
    );
  }

  return (
    <Card style={{ gridColumn: 'span 2' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <CardTitle style={{ margin: 0 }}>RECENT TRADES (Last 5)</CardTitle>
        <button onClick={() => onNavigate('journal')} style={{
          padding: '4px 12px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)',
          borderRadius: '6px', color: THEME.colors.blue, fontSize: '11px', cursor: 'pointer', fontWeight: '600',
        }}>View All →</button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              {['Dir', 'Date', 'Entry', 'SL', 'TP1', 'Exit', 'PnL', 'R:R', 'Grade', 'Result'].map(h => (
                <th key={h} style={{ padding: '6px 8px', color: THEME.colors.textMuted, fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.map((t, i) => (
              <tr key={t.id} style={{
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
              }}>
                {/* Direction */}
                <td style={{ padding: '8px 8px' }}>
                  <span style={{ color: dirColor(t.direction), fontWeight: '800', fontSize: '13px' }}>
                    {t.direction === 'BUY' ? '▲ B' : '▼ S'}
                  </span>
                </td>
                {/* Date */}
                <td style={{ padding: '8px 8px', color: THEME.colors.textMuted, whiteSpace: 'nowrap', fontSize: '11px' }}>
                  {t.date}<br/>
                  <span style={{ fontSize: '10px', color: 'rgba(148,163,184,0.5)' }}>{t.session || ''}</span>
                </td>
                {/* Entry */}
                <td style={{ padding: '8px 8px', color: THEME.colors.blue, fontWeight: '700', fontFamily: THEME.fonts.hud }}>
                  {t.entry ? `$${t.entry}` : '—'}
                </td>
                {/* SL */}
                <td style={{ padding: '8px 8px', color: '#f87171', fontWeight: '600', fontFamily: THEME.fonts.hud }}>
                  {t.sl ? `$${t.sl}` : '—'}
                </td>
                {/* TP1 */}
                <td style={{ padding: '8px 8px', color: THEME.colors.green, fontWeight: '600', fontFamily: THEME.fonts.hud }}>
                  {t.tp1 ? `$${t.tp1}` : '—'}
                </td>
                {/* Exit */}
                <td style={{ padding: '8px 8px', color: THEME.colors.text, fontFamily: THEME.fonts.hud }}>
                  {t.exit_price ? `$${t.exit_price}` : <span style={{ color: THEME.colors.textMuted }}>Open</span>}
                </td>
                {/* PnL */}
                <td style={{ padding: '8px 8px', fontWeight: '700', fontFamily: THEME.fonts.hud }}>
                  {t.pnl_usd != null
                    ? <span style={{ color: t.pnl_usd >= 0 ? THEME.colors.green : '#f87171' }}>
                        {t.pnl_usd >= 0 ? '+' : ''}${t.pnl_usd}
                      </span>
                    : <span style={{ color: THEME.colors.textMuted }}>—</span>
                  }
                </td>
                {/* R:R achieved */}
                <td style={{ padding: '8px 8px', color: '#ffd700', fontFamily: THEME.fonts.hud }}>
                  {t.rr_achieved ? `1:${t.rr_achieved}` : t.rr_planned ? <span style={{ color: THEME.colors.textMuted }}>~1:{t.rr_planned}</span> : '—'}
                </td>
                {/* Grade */}
                <td style={{ padding: '8px 8px' }}>
                  {t.setup_grade
                    ? <span style={{ color: gradeColor(t.setup_grade), fontWeight: '800', fontSize: '13px' }}>{t.setup_grade}</span>
                    : <span style={{ color: THEME.colors.textMuted }}>—</span>
                  }
                </td>
                {/* Result */}
                <td style={{ padding: '8px 8px' }}>
                  {t.result
                    ? <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontWeight: '700', fontSize: '11px',
                        background: `${resColor(t.result)}18`, color: resColor(t.result),
                        border: `1px solid ${resColor(t.result)}33`,
                      }}>{t.result}</span>
                    : <span style={{ color: '#ffd700', fontSize: '11px', fontWeight: '600' }}>OPEN</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── JARVIS Trade Plan Card ──────────────────────────────────────────────
// REPLACED (this session): the old "5-Agent" version asked Groq to
// *invent* HTF bias, entry/SL/TP levels, and a "jarvis_confidence" number
// via free-form prompts that literally included example numbers like
// "3285.00" — the LLM had every reason to anchor near those instead of
// reasoning from real price, and confidence scores were pure fabrication.
// It ALSO still referenced the old unvalidated 0.15/0.75/0.84/0.90 levels.
// This version calls analyzeAllTimeframes() — same real Structure+
// Fibonacci+ADX math as chat and Live Monitor, zero AI, zero guessing.
function TradePlanCard() {
  const [results, setResults] = useState(null);
  const [running,  setRunning]  = useState(false);
  const [error,    setError]    = useState('');

  const runCheck = async () => {
    setRunning(true);
    setResults(null);
    setError('');
    try {
      const r = await analyzeAllTimeframes();
      setResults(r);
    } catch (e) {
      setError(e.message || 'Signal check failed');
    } finally {
      setRunning(false);
    }
  };

  const actionColor = a => a === 'BUY' ? THEME.colors.green : a === 'SELL' ? THEME.colors.red : '#ffd700';
  const firm = (results || []).filter(r => r.ok && r.signal?.action !== 'WAIT');

  return (
    <Card style={{ gridColumn: 'span 2' }} glow={firm.length ? actionColor(firm[0].signal.action) : THEME.colors.blue}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <CardTitle color={THEME.colors.blue} style={{ margin: 0 }}>
          🎯 JARVIS REAL SIGNAL CHECK — Structure + Fibonacci + ADX (Zero AI)
        </CardTitle>
        <button
          onClick={runCheck}
          disabled={running}
          style={{
            padding:    '8px 20px', borderRadius: '8px', border: 'none',
            background: running ? 'rgba(0,212,255,0.08)' : THEME.colors.blue,
            color:      running ? THEME.colors.textMuted : '#000',
            fontSize:   '13px', fontWeight: '800', cursor: running ? 'not-allowed' : 'pointer',
            fontFamily: THEME.fonts.hud, letterSpacing: '1px', whiteSpace: 'nowrap',
          }}
        >
          {running ? '⏳ CHECKING...' : results ? '🔄 REFRESH' : '▶ CHECK ALL TIMEFRAMES'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: '8px', fontSize: '13px', color: '#f87171' }}>
          ⚠️ {error}
        </div>
      )}

      {results && !running && (
        <div>
          {results.map(({ tf, ok, signal, error: rowErr }) => (
            <div key={tf} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '6px',
              borderRadius: '8px',
              background: !ok ? 'rgba(255,255,255,0.02)' : signal.action === 'WAIT' ? 'rgba(255,255,255,0.02)' : `${actionColor(signal.action)}0c`,
              border: `1px solid ${!ok ? 'transparent' : signal.action === 'WAIT' ? 'transparent' : `${actionColor(signal.action)}30`}`,
            }}>
              <span style={{ width: '38px', fontSize: '12px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud }}>{tf}</span>
              {!ok ? (
                <span style={{ fontSize: '12px', color: '#f87171' }}>⚠️ {rowErr}</span>
              ) : signal.action === 'WAIT' ? (
                <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>⏸ WAIT — {signal.reason}</span>
              ) : (
                <span style={{ fontSize: '12px', color: THEME.colors.text }}>
                  <strong style={{ color: actionColor(signal.action) }}>{signal.action === 'BUY' ? '▲ BUY' : '▼ SELL'}</strong>
                  {' '}Entry ${signal.entry} · SL ${signal.sl} · T1 ${signal.target} · R:R {signal.rr}
                </span>
              )}
            </div>
          ))}

          <div style={{ marginTop: '10px', fontSize: '12px', color: firm.length ? THEME.colors.green : THEME.colors.textMuted, fontStyle: firm.length ? 'normal' : 'italic' }}>
            {firm.length === 0
              ? 'कुठल्याही timeframe वर सध्या सगळे gates (structure + OTE zone + ADX trending + HTF match + confirmation candle) एकत्र जुळलेले नाहीत — हेच खरं गणित आहे.'
              : `${firm.length} timeframe(s) वर खरा setup — Trade Journal मध्ये demo trade म्हणून auto-save झालाय.`}
          </div>
        </div>
      )}

      {!results && !running && !error && (
        <div style={{ textAlign: 'center', padding: '30px 0', color: THEME.colors.textMuted }}>
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>🎯</div>
          <div style={{ fontSize: '14px', marginBottom: '6px', color: THEME.colors.text }}>Real Structure+Fibonacci+ADX Check</div>
          <div style={{ fontSize: '12px', lineHeight: '1.7' }}>
            Click <strong style={{ color: THEME.colors.blue }}>CHECK ALL TIMEFRAMES</strong> — 15m/30m/45m/1h/4h, real Yahoo Finance data, zero AI guessing.
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Quick Navigation Grid ─────────────────────────────────────────────
function QuickNav({ onNavigate }) {
  const links = [
    { id: 'chat',       icon: '🤖', label: 'Ask JARVIS'  },
    { id: 'daily',      icon: '📚', label: 'Daily Recap' },
    { id: 'bridge',     icon: '🌉', label: 'Bridge'      },
    { id: 'journal',    icon: '📓', label: 'Journal'     },
    { id: 'rules',      icon: '📋', label: 'Rules'       },
    { id: 'risk',       icon: '💰', label: 'Risk Calc'   },
  ];
  return (
    <Card style={{ gridColumn: 'span 2' }}>
      <CardTitle>QUICK ACTIONS</CardTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
        {links.map(l => (
          <button key={l.id} onClick={() => onNavigate(l.id)} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
            padding: '12px 8px', background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px',
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
            <span style={{ fontSize: '22px' }}>{l.icon}</span>
            <span style={{ fontSize: '11px', color: THEME.colors.textMuted, letterSpacing: '0.5px' }}>{l.label}</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────
export default function Dashboard() {
  const { dispatch } = useJarvisContext();
  const navigate = id => dispatch({ type: A.SET_SCREEN, payload: id });

  return (
    <div style={{
      padding:    '16px',
      display:    'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap:        '12px',
      alignContent: 'start',
    }}>
      <PriceHero      onNavigate={navigate} />
      <TradePlanCard />
      <EvolutionCard />
      <StatsCard      onNavigate={navigate} />
      <KnowledgeCard />
      <TodayCard      onNavigate={navigate} />
      <BrainCard />
      <RecentTradesCard onNavigate={navigate} />
      <QuickNav       onNavigate={navigate} />
    </div>
  );
}
